import { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, ChevronDown, ChevronUp, Clock, ExternalLink, FileText, Loader2, Send, Sparkles, Star, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';

interface AgentSource {
  id: string;
  type: 'page' | 'tile' | 'form';
  title: string;
  snippet: string;
  url: string;
  pagePath: string[];
}

interface AgentAction {
  label: string;
  url: string;
  type: 'answer' | 'navigate' | 'form';
  sourceId?: string;
}

interface AgentAnswer {
  answer: string;
  confidence: 'high' | 'medium' | 'low';
  actions: AgentAction[];
  sources: AgentSource[];
}

interface NexusAgentPopoverProps {
  compact?: boolean;
}

interface AgentHistoryItem {
  id: string;
  question: string;
  answer: AgentAnswer;
  createdAt: string;
  favorite: boolean;
}

const HISTORY_STORAGE_KEY = 'nexus-agent-history';
const MAX_HISTORY_ITEMS = 20;
const DEFAULT_HISTORY_LIMIT = 4;

const EXAMPLE_QUESTIONS = [
  'How much leave do I get each year?',
  'Where is the expense claim form?',
  'What product features are still on the roadmap?',
  'Show me the onboarding process for new staff',
  'Where can I find the working from home policy?',
];

export default function NexusAgentPopover({ compact = false }: NexusAgentPopoverProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<AgentAnswer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<AgentHistoryItem[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const exampleQuestion = useMemo(
    () => EXAMPLE_QUESTIONS[Math.floor(Math.random() * EXAMPLE_QUESTIONS.length)],
    [isOpen]
  );

  useEffect(() => {
    try {
      const storedHistory = window.localStorage.getItem(HISTORY_STORAGE_KEY);
      if (storedHistory) {
        const parsedHistory = JSON.parse(storedHistory);
        if (Array.isArray(parsedHistory)) {
          setHistory(parsedHistory.slice(0, MAX_HISTORY_ITEMS));
        }
      }
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!historyLoaded) return;

    try {
      window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history.slice(0, MAX_HISTORY_ITEMS)));
    } catch {
      // History is helpful, but the agent should still work if storage is unavailable.
    }
  }, [history, historyLoaded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closePopover();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(question.length, question.length);
      }, 50);
    }
  }, [isOpen, question.length]);

  const saveHistoryItem = (submittedQuestion: string, agentAnswer: AgentAnswer) => {
    setHistory((currentHistory) => {
      const existing = currentHistory.find((item) => item.question.toLowerCase() === submittedQuestion.toLowerCase());
      const nextItem: AgentHistoryItem = {
        id: existing?.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        question: submittedQuestion,
        answer: agentAnswer,
        createdAt: new Date().toISOString(),
        favorite: existing?.favorite || false,
      };

      return [
        nextItem,
        ...currentHistory.filter((item) => item.id !== nextItem.id && item.question.toLowerCase() !== submittedQuestion.toLowerCase()),
      ].slice(0, MAX_HISTORY_ITEMS);
    });
  };

  const handleHistorySelect = (item: AgentHistoryItem) => {
    setQuestion(item.question);
    setAnswer(item.answer);
    setError(null);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(item.question.length, item.question.length);
    }, 0);
  };

  const toggleFavorite = (itemId: string) => {
    setHistory((currentHistory) =>
      currentHistory.map((item) => (item.id === itemId ? { ...item, favorite: !item.favorite } : item))
    );
  };

  const resetActiveRequest = () => {
    setQuestion('');
    setAnswer(null);
    setError(null);
    setShowAllHistory(false);
  };

  const closePopover = () => {
    setIsOpen(false);
    resetActiveRequest();
  };

  const togglePopover = () => {
    if (isOpen) {
      closePopover();
    } else {
      resetActiveRequest();
      setIsOpen(true);
    }
  };

  const handleSubmit = async () => {
    const trimmedQuestion = question.trim();
    if (trimmedQuestion.length < 3 || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/agent/query', { question: trimmedQuestion }, { timeout: 90000 });
      const agentAnswer = response.data.data;
      setAnswer(agentAnswer);
      saveHistoryItem(trimmedQuestion, agentAnswer);
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to ask the Nexus agent');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigate = (url: string) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank');
      return;
    }

    navigate(url);
    closePopover();
  };

  const buttonClass = compact
    ? 'p-1 hover:bg-muted rounded-md'
    : 'p-2 hover:bg-muted rounded-md';

  const orderedHistory = [
    ...history.filter((item) => item.favorite),
    ...history.filter((item) => !item.favorite),
  ];
  const visibleHistory = showAllHistory ? orderedHistory : orderedHistory.slice(0, DEFAULT_HISTORY_LIMIT);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={togglePopover}
        className={buttonClass}
        title="Ask Nexus"
        aria-label="Ask Nexus"
      >
        <Bot className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-muted-foreground`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[min(calc(100vw-1rem),440px)] max-h-[min(720px,calc(100vh-5rem))] bg-background border border-border rounded-lg shadow-lg z-50 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="font-medium text-sm truncate">Ask Nexus</span>
            </div>
            <button
              type="button"
              onClick={closePopover}
              className="p-1 hover:bg-muted rounded-md"
              title="Close"
              aria-label="Close Ask Nexus"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          <div className="p-4 space-y-3 overflow-y-auto">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSubmit();
                  }
                }}
                rows={3}
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={exampleQuestion}
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={question.trim().length < 3 || isLoading}
                className="absolute bottom-2 right-2 p-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90"
                title="Ask"
                aria-label="Ask Nexus"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {answer && (
              <div className="space-y-3">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <p className="text-sm whitespace-pre-wrap leading-6">{answer.answer}</p>
                </div>

                {answer.actions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {answer.actions.map((action, index) => (
                      <button
                        key={`${action.url}-${index}`}
                        type="button"
                        onClick={() => handleNavigate(action.url)}
                        className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                      >
                        {action.type === 'form' ? <FileText className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
                        <span className="max-w-[220px] truncate">{action.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {answer.sources.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">Sources</div>
                    {answer.sources.slice(0, 4).map((source) => (
                      <button
                        key={source.id}
                        type="button"
                        onClick={() => handleNavigate(source.url)}
                        className="w-full text-left rounded-md px-2 py-2 hover:bg-muted"
                      >
                        <div className="text-sm font-medium truncate">{source.title}</div>
                        {source.pagePath.length > 0 && (
                          <div className="text-xs text-muted-foreground truncate">{source.pagePath.join(' > ')}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {history.length > 0 && (
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Past Requests</span>
                  </div>
                  {orderedHistory.length > DEFAULT_HISTORY_LIMIT && (
                    <button
                      type="button"
                      onClick={() => setShowAllHistory((value) => !value)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      {showAllHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {showAllHistory ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>

                <div className="space-y-1">
                  {visibleHistory.map((item) => (
                    <div key={item.id} className="flex items-center gap-1 rounded-md hover:bg-muted">
                      <button
                        type="button"
                        onClick={() => handleHistorySelect(item)}
                        className="min-w-0 flex-1 text-left px-2 py-2"
                      >
                        <div className="text-sm font-medium truncate">{item.question}</div>
                        <div className="text-xs text-muted-foreground truncate">{item.answer.answer}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleFavorite(item.id)}
                        className="p-2 rounded-md text-muted-foreground hover:text-foreground"
                        title={item.favorite ? 'Remove favourite' : 'Favourite request'}
                        aria-label={item.favorite ? 'Remove favourite request' : 'Favourite request'}
                      >
                        <Star className={`h-4 w-4 ${item.favorite ? 'fill-yellow-400 text-yellow-500' : ''}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
